/**
 * Registry of all components available for configurable pages
 * Single source of truth for component mapping
 */

// Import all components that can be used in configurable pages
import Action_Table from '@/components/Lists/Action_Table.vue'
import MetricInsightsWidget from '@/components/Metric/MetricInsightsWidget.vue'
import TOCAdtEpisodesInsight from '@/components/Metric/Metric_Insights/51_ADT_EPISODES.vue'
import Engagement_Table from '@/components/Lists/Engagement_Table.vue'
import ScheduledMeeting_Table from '@/components/Lists/ScheduledMeeting_Table.vue'
import UserInfoCard from '@/components/ConfigurablePageComponents/UserInfoCard.vue'
import NextMeetings_List from '@/components/ConfigurablePageComponents/NextMeetings_List.vue'
import Portfolio_Rollup from '@/components/ConfigurablePageComponents/Portfolio_Rollup.vue'
import PracticeConstraints_Table from '@/components/ConfigurablePageComponents/PracticeConstraints_Table.vue'
import Practice_Tag_Summary from '@/components/Lists/Practice_Tag_Summary.vue'
import Project_Table from '@/components/Lists/Project_Table.vue'
import SurveyQuestionSummaryTable from '@/components/Lists/SurveyQuestionSummary_Table.vue'
import EngagementStatsOverview from '@/components/ConfigurablePageComponents/Engage/EngagementStatsOverview.vue'
import TabbedEntitySelector from '@/components/ConfigurablePageComponents/TabbedEntitySelector.vue'
import TabbedEngagementTable from '@/components/ConfigurablePageComponents/TabbedEngagementTable.vue'
import ConstraintThemes from '@/components/ConfigurablePageComponents/Engage/ConstraintThemes.vue'
import TabbedActionTable from '@/components/ConfigurablePageComponents/TabbedActionTable.vue'
import TabbedScheduledMeetingTable from '@/components/ConfigurablePageComponents/TabbedScheduledMeetingTable.vue'
import EngagementVelocity from '@/components/ConfigurablePageComponents/Engage/EngagementVelocity.vue'
import EngagementCoverage from '@/components/ConfigurablePageComponents/Engage/EngagementCoverage.vue'
import OnboardingPipeline from '@/components/ConfigurablePageComponents/Engage/OnboardingPipeline.vue'
import EntityHierarchyNavigatorV2 from '@/components/ConfigurablePageComponents/EntityHierarchyNavigatorV2.vue'
import PerformanceTrend from '@/components/InsightsNew/PerformanceTrend.vue'
import PerformanceCohort from '@/components/InsightsNew/PerformanceCohort.vue'
import PerformanceSummary from '@/components/InsightsNew/PerformanceSummary.vue'
import PerformanceRankings from '@/components/InsightsNew/PerformanceRankings.vue'
import PatientWorklist from '@/components/InsightsNew/PatientWorklist.vue'
import ComposableAlertTable from '@/components/InsightsNew/ComposableAlertTable.vue'
import PracticePerformanceList from '@/components/InsightsNew/PracticePerformanceList.vue'
import PerformanceTrajectory from '@/components/InsightsNew/PerformanceTrajectory.vue'
import OverallPerformanceDashboard from '@/components/InsightsNew/OverallPerformanceDashboard.vue'
import ExecSummaryPlaceholder from '@/components/InsightsNew/ExecSummaryPlaceholder.vue'
import PerformanceSignalStudy from '@/components/InsightsNew/PerformanceSignalStudy.vue'
import CuranaContractAssessment from '@/components/InsightsNew/CuranaContractAssessment.vue'

import Attribution_Page from '@/components/Attribution/Attribution_Page.vue'
import Attribution_Health from '@/components/Entity/Attribution_Health.vue'
import CostAttributionFlow from '@/components/InsightsNew/CostAttributionFlow.vue'
import PanelManagementTabs from '@/components/InsightsNew/PanelManagementTabs.vue'
import CCLFReport from '@/components/External/CCLFReport/CCLFReport.vue'
import ExternalFocusItems from '@/components/External/ExternalFocusItems.vue'
import AIInsights from '@/components/InsightsNew/AIInsights.vue'
import ACSCCascadeSankey from '@/components/InsightsNew/ACSCCascadeSankey.vue'
import DataFreshnessDashboard from '@/components/InsightsNew/DataFreshnessDashboard.vue'
import FeedRegistry from '@/components/ETL_Utility/Overview_ETL_Feed_Registry.vue'
import NetworkMap from '@/components/NetworkMap/NetworkMap.vue'
import UserPhoneNumbers from '@/components/ConfigurablePageComponents/UserPhoneNumbers.vue'

/**
 * Component Registry
 * Maps component identifiers to actual Vue components and metadata
 */
export const COMPONENT_REGISTRY = {
  'UserInfoCard': {
    component: UserInfoCard,
    displayName: 'User Info Card',
    description: 'Displays user information with avatar, details, and action buttons',
    category: 'User',
    defaultSettings: {
      show_avatar: true,
      show_roles: true,
      show_last_login: true
    }
  },
  'NextMeetings_List': {
    component: NextMeetings_List,
    displayName: 'For Your Next Meetings',
    description: 'Open gaps and action items at every practice the linked entity manages, grouped by practice and ordered by who is due to be met next',
    category: 'User',
    defaultSettings: {
      entity_id: 'LINKED_ENTITY_ID'
    }
  },
  'Portfolio_Rollup': {
    component: Portfolio_Rollup,
    displayName: 'Portfolio Rollup',
    description: 'Open gaps and action items under the linked entity, grouped by the manager of each practice',
    category: 'User',
    defaultSettings: {
      entity_id: 'LINKED_ENTITY_ID'
    }
  },
  'PracticeConstraints_Table': {
    component: PracticeConstraints_Table,
    displayName: 'Constraints & Actions by Practice',
    description: 'The practices the linked entity manages, with open constraint/action and prioritized counts, filterable to manager-prioritized work and sortable by name or upcoming engagement',
    category: 'User',
    defaultSettings: {
      entity_id: 'LINKED_ENTITY_ID'
    }
  },
  'Action_Table': {
    component: Action_Table,
    displayName: 'Action Table',
    description: 'Displays action items with filtering options',
    category: 'Lists',
    defaultSettings: {
      auto_height: true,
      completed_date_is_null: true
    }
  },
  'Engagement_Table': {
    component: Engagement_Table,
    displayName: 'Engagement Table',
    description: 'Displays meetings and engagements',
    category: 'Lists',
    defaultSettings: {
      auto_height: true
    }
  },
  'ScheduledMeeting_Table': {
    component: ScheduledMeeting_Table,
    displayName: 'Upcoming Meetings Table',
    description: 'Displays upcoming scheduled meetings from calendar',
    category: 'Lists',
    defaultSettings: {
      auto_height: true
    }
  },
  'AlertInstance_Table': {
    component: ComposableAlertTable,
    displayName: 'Alert Table',
    description: 'Displays metric alerts and notifications',
    category: 'Lists',
    defaultSettings: {}
  },
  'Practice_Tag_Summary': {
    component: Practice_Tag_Summary,
    displayName: 'Entity Summary',
    description: 'Tag-driven entity explorer across any entity type. Group/filter by hierarchy and tag, with engagement, attribution, and tag-coverage reporting.',
    category: 'Lists',
    defaultSettings: {}
  },
  'Project_Table': {
    component: Project_Table,
    displayName: 'Project Table',
    description: 'Displays projects and their details',
    category: 'Lists',
    defaultSettings: {
      grouperColumns: ['project_name', 'action_name', 'appuser_name', 'practice_name']
    }
  },

  'SurveyQuestionSummaryTable': {
    component: SurveyQuestionSummaryTable,
    displayName: 'Survey Summary Table',
    description: 'Displays survey question summaries',
    category: 'Lists',
    defaultSettings: {
      auto_height: true
    }
  },
  'EngagementStatsOverview': {
    component: EngagementStatsOverview,
    displayName: 'Engagement Stats Overview',
    description: 'Displays engagement velocity and coverage analytics with interactive tabs',
    category: 'Analytics',
    defaultSettings: {}
  },
  'TabbedEntitySelector': {
    component: TabbedEntitySelector,
    displayName: 'Tabbed Entity Selector',
    description: 'Entity table with My Practices/All Practices/By Group tabs',
    category: 'Market',
    defaultSettings: {
      tabs: ['My Practices', 'All Practices', 'By Group'],
      default_tab: 'By Group',
      localStorage_key: 'engage_entity_tab',
      show_group_type_selector: true
    }
  },
  'ConstraintThemes': {
    component: ConstraintThemes,
    displayName: 'Constraint Themes',
    description: 'Sentence-level themes combining the specific constraints across a grouping\'s practices, with drill-down to each constraint',
    category: 'Market',
    defaultSettings: {
      localStorage_key: 'engage_theme_grouping'
    }
  },
  'TabbedEngagementTable': {
    component: TabbedEngagementTable,
    displayName: 'Tabbed Engagement Table',
    description: 'Engagement table with My Practices/All Practices tabs',
    category: 'Market',
    defaultSettings: {
      tabs: ['My Practices', 'All Practices'],
      default_tab: 'My Practices',
      localStorage_key: 'engage_engagement_tab'
    }
  },
  'TabbedActionTable': {
    component: TabbedActionTable,
    displayName: 'Tabbed Action Table',
    description: 'Action items table with My Practices/All Practices tabs and completed toggle',
    category: 'Market',
    defaultSettings: {
      tabs: ['My Practices', 'All Practices'],
      default_tab: 'My Practices',
      localStorage_key: 'engage_actions_tab',
      show_completed_toggle: true,
      default_show_completed: false
    }
  },
  'TabbedScheduledMeetingTable': {
    component: TabbedScheduledMeetingTable,
    displayName: 'Tabbed Scheduled Meeting Table',
    description: 'Upcoming meetings table with My Meetings/All Meetings tabs',
    category: 'Market',
    defaultSettings: {
      tabs: ['My Meetings', 'All Meetings'],
      default_tab: 'My Meetings',
      localStorage_key: 'scheduled_meeting_tab'
    }
  },
  'EngagementVelocity': {
    component: EngagementVelocity,
    displayName: 'Engagement Velocity',
    description: 'Engagement trends and analytics with collapsible detail view',
    category: 'Engagement',
    canExpand: true,
    defaultSettings: {
      persist_key: 'engage_velocity'
    }
  },
  'EngagementCoverage': {
    component: EngagementCoverage,
    displayName: 'Practice Coverage',
    description: 'Practice and attribution coverage analytics with collapsible detail view',
    category: 'Engagement',
    canExpand: true,
    defaultSettings: {
      persist_key: 'engage_coverage'
    }
  },
  'OnboardingPipeline': {
    component: OnboardingPipeline,
    displayName: 'Onboarding Pipeline',
    description: 'Track practices through onboarding stages with at-risk indicators',
    category: 'Engagement',
    canExpand: true,
    defaultSettings: {
      persist_key: 'onboarding_pipeline',
      infotag_type: 'Implementation Status',
      exclude_tags: 'Live',
      risk_threshold: 45,
      critical_threshold: 75
    }
  },
  'EntityHierarchyNavigatorV2': {
    component: EntityHierarchyNavigatorV2,
    displayName: 'Entity Hierarchy Navigator',
    description: 'Crosswalk-driven entity navigator with correct per-cohort attribution',
    category: 'Market',
    defaultSettings: {
      localStorage_key: 'entity_hierarchy_nav_v2',
      columns: ['tags', 'last_contact', 'score', 'attribution'],
      show_search: true
    }
  },
  'PerformanceTrend': {
    component: PerformanceTrend,
    displayName: 'Performance Trend',
    description: 'Time series bar+line chart with prior year and EFCTV comparison',
    category: 'Metrics',
    defaultSettings: {}
  },
  'PerformanceCohort': {
    component: PerformanceCohort,
    displayName: 'Peer Comparison',
    description: 'Horizontal bar chart comparing entities on a metric',
    category: 'Metrics',
    defaultSettings: {}
  },
  'PerformanceSummary': {
    component: PerformanceSummary,
    displayName: 'Performance Summary',
    description: 'Entity-level summary table with dimensions, filters, and drill-down',
    category: 'Metrics',
    defaultSettings: {}
  },
  'PerformanceRankings': {
    component: PerformanceRankings,
    displayName: 'Performance Rankings',
    description: 'Per-metric ranked view showing top / bottom performers and movers.',
    category: 'Metrics',
    defaultSettings: {
      self_chrome: true
    }
  },
  'PatientWorklist': {
    component: PatientWorklist,
    displayName: 'Patient Worklist',
    description: 'Composable patient list with configurable columns from DataAttribute primitives. Column selection changes output grain. Excel export.',
    category: 'Metrics',
    defaultSettings: {}
  },
  'ComposableAlertTable': {
    component: ComposableAlertTable,
    displayName: 'Composable Alerts',
    description: 'Alert instances for composable metrics. Shows entity-level alerts with severity, status management, and assignment.',
    category: 'Metrics',
    defaultSettings: {}
  },
  'PracticePerformanceList': {
    component: PracticePerformanceList,
    displayName: 'Practice Performance List',
    description: 'Ranked practice list (top/bottom/blocked) from automated analysis',
    category: 'Performance',
    defaultSettings: {
      listType: 'top_performers'
    }
  },
  'PerformanceTrajectory': {
    component: PerformanceTrajectory,
    displayName: 'Performance Trajectory',
    description: 'Panel-weighted composite percentile over time across all practices',
    category: 'Performance',
    defaultSettings: {}
  },
  'OverallPerformanceDashboard': {
    component: OverallPerformanceDashboard,
    displayName: 'Overall Performance Dashboard',
    description: 'Contract-scoped trajectory chart and ranked practice lists (top/bottom/blocked)',
    category: 'Performance',
    defaultSettings: {}
  },
  'CuranaContractAssessment': {
    component: CuranaContractAssessment,
    displayName: 'AI Contract Assessment (Curana A5069)',
    description: 'Temporary widget — AI-generated forensic cost memo for Curana A5069 with download-to-PDF',
    category: 'Performance',
    defaultSettings: {}
  },
  'ExecSummaryPlaceholder': {
    component: ExecSummaryPlaceholder,
    displayName: 'Executive Summary',
    description: 'Placeholder panel reserving space for the future cohort-adjusted exec summary widget. Renders empty content inside standard panel chrome.',
    category: 'Performance',
    // Bare content — the standard panel wrapper (border + titled header) is
    // applied by InsightsNew_MetricView so it matches sibling panels.
    selfChrome: false,
    defaultSettings: {}
  },
  'PerformanceSignalStudy': {
    component: PerformanceSignalStudy,
    displayName: 'Performance Signal Study',
    description: 'Practice-liaison constraints extracted from meeting transcripts, with interventions, resolution status and verbatim evidence. Practice list left, constraint detail right.',
    category: 'Performance',
    // Draws its own card + header row (same shape as AI Insights), so the host
    // page must NOT wrap it in the standard titled panel.
    selfChrome: true,
    defaultSettings: {}
  },

  'FocusAreas': {
    component: ExternalFocusItems,
    displayName: 'Focus Areas',
    description: 'Tenant-wide Focus Areas (Requirement type 40). Reuses the External portal Focus Items widget with NO entity scope — pass no entityId so it shows every focus item for the tenant. The widget is headerless (its title/header lives on the host page), so ConfigurablePage supplies the standard titled panel (self_chrome false).',
    category: 'Engage',
    // On Engage the component draws its OWN card + header row (title left,
    // Add/Show-Archived buttons right) as one flush unit, so ConfigurablePage
    // renders it bare (self_chrome true) and we pass a `title` via settings.
    selfChrome: true,
    defaultSettings: { self_chrome: true, title: 'Focus Areas' },
  },

  'MetricInsights': {
    component: MetricInsightsWidget,
    displayName: 'Metric Insights',
    description: 'Data-driven drill-down insights for the metric (DB-configured)',
    category: 'Insights',
    defaultSettings: {}
  },
  'TOCAdtEpisodesInsight': {
    component: TOCAdtEpisodesInsight,
    displayName: 'Transitions of Care',
    description: 'Dedicated Transitions-of-Care widget for metric 51 (ADT episodes + follow-up Sankey with date/drill filters). Replaces the generic MetricInsights widget, which would fire all 8 heavy metric-51 insights at once.',
    category: 'Insights',
    // Bare content — the insight page provides the standard panel chrome
    // (border + titled header) so it matches sibling sections automatically.
    selfChrome: false,
    defaultSettings: {}
  },
  'DataFreshnessDashboard': {
    component: DataFreshnessDashboard,
    displayName: 'Data Freshness Dashboard',
    description: 'Claims data completeness and timeliness by practice',
    category: 'Performance',
    defaultSettings: {}
  },
  'FeedRegistry': {
    component: FeedRegistry,
    displayName: 'Data Feed Registry',
    description: 'Per-tenant feed freshness/health dashboard grouped by data domain',
    category: 'Performance',
    defaultSettings: {}
  },
  'Attribution_Page': {
    component: Attribution_Page,
    displayName: 'Contract Attribution Manifest',
    description: 'Contract manifest showing payer, LOB, patients, practices, TINs by performance year',
    category: 'Performance',
    defaultSettings: {}
  },
  'Attribution_Health': {
    component: Attribution_Health,
    displayName: 'Attribution Health',
    description: 'Attribution flow Sankey comparing two report periods with patient drill-down',
    category: 'Performance',
    defaultSettings: {}
  },
  'CostAttributionFlow': {
    component: CostAttributionFlow,
    displayName: 'Cost Attribution Flow',
    description: 'Decomposes panel cost changes into leavers/stayers/joiners by practice with BEUR costs',
    category: 'Performance',
    defaultSettings: {}
  },
  'PanelManagementTabs': {
    component: PanelManagementTabs,
    displayName: 'Panel Management (Tabbed)',
    description: 'Attribution Health + Cost Attribution in tabs',
    category: 'Performance',
    defaultSettings: {}
  },
  'ACSCCascadeSankey': {
    component: ACSCCascadeSankey,
    displayName: 'ACSC Cascade Sankey',
    description: 'Avoidable hospitalization cascade: Facility → ED → IP → SNF',
    category: 'Performance',
    defaultSettings: {}
  },
  'CCLFReport': {
    component: CCLFReport,
    displayName: 'CCLF Monthly Report',
    description: 'Curana monthly cost report with tabbed views for cost/utilization, DRG, hospice, patient detail, and HCC gaps',
    category: 'Performance',
    defaultSettings: {}
  },
  'AIInsights': {
    component: AIInsights,
    displayName: 'AI Insights',
    description: 'Displays findings from structured AI analysis runs with patient and claim-level evidence',
    category: 'Performance',
    defaultSettings: {}
  },
  'NetworkMap': {
    component: NetworkMap,
    displayName: 'Network Map',
    description: 'Interactive geographic map of practices with filtering, search, and color/size encoding',
    category: 'Market',
    defaultSettings: {
      self_chrome: true
    }
  },
  'UserPhoneNumbers': {
    component: UserPhoneNumbers,
    displayName: 'Phone Numbers',
    description: 'Manage verified personal phone numbers for outbound caller ID',
    category: 'User',
    defaultSettings: {}
  },
}

/**
 * Get component by identifier
 * @param {string} componentId - Component identifier
 * @returns {Object|null} Component object or null if not found
 */
export function getComponent(componentId) {
  const entry = COMPONENT_REGISTRY[componentId]
  return entry ? entry.component : null
}

/**
 * Get the full registry entry (metadata) for a component id, so callers can
 * read presentation hints like `selfChrome` (the component draws its own
 * card/title) and `displayName` (the panel title to apply when it does not).
 * @param {string} componentId
 * @returns {object|null}
 */
export function getComponentMeta(componentId) {
  return COMPONENT_REGISTRY[componentId] || null
}

/**
 * Get all available components
 * @returns {Array} Array of component metadata
 */
export function getAllComponents() {
  return Object.entries(COMPONENT_REGISTRY).map(([id, data]) => ({
    id,
    displayName: data.displayName,
    description: data.description,
    category: data.category,
    defaultSettings: data.defaultSettings
  }))
}

/**
 * Get components grouped by category
 * @returns {Object} Components grouped by category
 */
export function getComponentsByCategory() {
  const grouped = {}
  Object.entries(COMPONENT_REGISTRY).forEach(([id, data]) => {
    const category = data.category || 'Other'
    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category].push({
      id,
      displayName: data.displayName,
      description: data.description,
      defaultSettings: data.defaultSettings
    })
  })
  return grouped
}

/**
 * Get default settings for a component
 * @param {string} componentId - Component identifier
 * @returns {Object} Default settings
 */
export function getDefaultSettings(componentId) {
  const entry = COMPONENT_REGISTRY[componentId]
  return entry ? { ...entry.defaultSettings } : {}
}

/**
 * Check if a component supports expand/collapse
 * @param {string} componentId - Component identifier
 * @returns {boolean} Whether component can expand
 */
export function canComponentExpand(componentId) {
  const entry = COMPONENT_REGISTRY[componentId]
  return entry?.canExpand === true
}
